// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  admin/src/pages/GroceryStores.jsx  —  Quickry Admin
//  Blinkit-style Category System
//  Copy to: C:\quickry\admin\src\pages\GroceryStores.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect, useRef } from "react";
import {
  Plus, Edit2, Trash2, Search, ShoppingBasket,
  Check, X, Loader2, ToggleLeft, ToggleRight,
  RefreshCw, AlertCircle, MapPin, Phone,
  Clock, Truck, Package, Navigation, ChevronRight,
  Tag, Layers, FolderOpen, ChevronDown, Upload, Download, FileSpreadsheet,
} from "lucide-react";

const T = {
  primary:"#f97316", primaryDark:"#ea580c", primaryLight:"#fff7ed", primaryMid:"#fed7aa",
  white:"#ffffff", bg:"#f1f5f9", text:"#0f172a", textSub:"#64748b", textMuted:"#94a3b8",
  border:"#e2e8f0", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444", redLight:"#fef2f2",
  yellow:"#f59e0b", blue:"#2563eb", blueLight:"#eff6ff", purple:"#7c3aed", purpleLight:"#f5f3ff",
};

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";
const getToken = () => localStorage.getItem("adminToken") || localStorage.getItem("token") || "";

const apiCall = async (method, url, body = null) => {
  try {
    const res = await fetch(`${API}${url}`, {
      method,
      headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return await res.json();
  } catch (e) { return { success:false, message:e.message }; }
};

// ── Default Blinkit-style categories (seed data) ──
const DEFAULT_MAIN_CATS = [
  { name:"Grocery & Kitchen",       icon:"🛒", color:"#16a34a" },
  { name:"Snacks & Drinks",         icon:"🥤", color:"#f97316" },
  { name:"Beauty & Personal Care",  icon:"💄", color:"#db2777" },
  { name:"Household & Cleaning",    icon:"🧹", color:"#2563eb" },
  { name:"Baby & Kids",             icon:"👶", color:"#7c3aed" },
  { name:"Pet Care",                icon:"🐾", color:"#92400e" },
];

const EMPTY_STORE = {
  name:"", area:"", address:"", phone:"",
  categories:"", deliveryTime:45, deliveryFee:30,
  minOrder:199, isOpen:true, isActive:true, lat:"", lng:"", radius:10,
};

// ── Input Field ───────────────────────────────────
function F({ label, k, type="text", placeholder="", form, set }) {
  return (
    <div>
      <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>{label}</div>
      <input
        type={type}
        value={form[k] ?? ""}
        onChange={e => set(k, type==="number" ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder}
        style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", marginBottom:12, boxSizing:"border-box" }}
      />
    </div>
  );
}

// ── Select Field ──────────────────────────────────
function Select({ label, value, onChange, options, placeholder }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:value?T.text:T.textMuted, background:T.white, outline:"none", fontFamily:"inherit", cursor:"pointer" }}>
        <option value="">{placeholder || "Select..."}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ════════════════════════════════════════════════
// CATEGORY MANAGER MODAL
// ════════════════════════════════════════════════
function CategoryManager({ onClose }) {
  const [cats,       setCats]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [activeMain, setActiveMain] = useState(null);
  const [tab,        setTab]        = useState("list"); // list | addMain | addSub
  const [saving,     setSaving]     = useState(false);

  // Forms
  const [mainForm, setMainForm] = useState({ name:"", icon:"🛒", color:"#16a34a", image:"" });
  const [subForm,  setSubForm]  = useState({ name:"", icon:"📦", mainCategory:"", image:"" });
  const [editItem, setEditItem] = useState(null); // { type:"main"|"sub", data }

  const loadCats = async () => {
    setLoading(true);
    const res = await apiCall("GET", "/categories");
    if (res.success) setCats(res.categories || []);
    setLoading(false);
  };

  useEffect(() => { loadCats(); }, []);

  // Seed default categories
  const seedDefaults = async () => {
    if (!window.confirm("Blinkit-style default categories add karo?")) return;
    setSaving(true);
    for (const cat of DEFAULT_MAIN_CATS) {
      await apiCall("POST", "/categories/main", cat);
    }
    await loadCats();
    setSaving(false);
  };

  const saveMain = async () => {
    if (!mainForm.name) return alert("Name required!");
    setSaving(true);
    let res;
    if (editItem?.type === "main") {
      res = await apiCall("PUT", `/categories/main/${editItem.data._id}`, mainForm);
    } else {
      res = await apiCall("POST", "/categories/main", mainForm);
    }
    setSaving(false);
    if (res.success) {
      await loadCats();
      setTab("list"); setMainForm({ name:"", icon:"🛒", color:"#16a34a" }); setEditItem(null);
    } else alert(res.message);
  };

  const saveSub = async () => {
    if (!subForm.name || !subForm.mainCategory) return alert("Name aur main category required!");
    setSaving(true);
    let res;
    if (editItem?.type === "sub") {
      res = await apiCall("PUT", `/categories/sub/${editItem.data._id}`, subForm);
    } else {
      res = await apiCall("POST", "/categories/sub", subForm);
    }
    setSaving(false);
    if (res.success) {
      await loadCats();
      setTab("list"); setSubForm({ name:"", icon:"📦", mainCategory:"" }); setEditItem(null);
    } else alert(res.message);
  };

  const deleteMain = async (id) => {
    if (!window.confirm("Main category aur uske sab sub categories delete honge!")) return;
    await apiCall("DELETE", `/categories/main/${id}`);
    await loadCats();
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Sub category delete karein?")) return;
    await apiCall("DELETE", `/categories/sub/${id}`);
    await loadCats();
  };

  const startEditMain = (cat) => {
    setMainForm({ name:cat.name, icon:cat.icon||"🛒", color:cat.color||"#16a34a", image:cat.image||"" });
    setEditItem({ type:"main", data:cat }); setTab("addMain");
  };

  const startEditSub = (sub, mainId) => {
    setSubForm({ name:sub.name, icon:sub.icon||"📦", mainCategory:mainId, image:sub.image||"" });
    setEditItem({ type:"sub", data:sub }); setTab("addSub");
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.white, borderRadius:20, width:"100%", maxWidth:600, maxHeight:"90vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div style={{ padding:"18px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1rem", color:T.text }}>Category Manager</div>
            <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>Blinkit-style category system</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted }}><X size={20}/></button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}`, overflowX:"auto" }}>
          {[
            { id:"list",    label:"Categories"    },
            { id:"addMain", label:editItem?.type==="main" ? "Edit Main" : "+ Main" },
            { id:"addSub",  label:editItem?.type==="sub"  ? "Edit Sub"  : "+ Sub"  },
          ].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); if(t.id!=="list") setEditItem(null); }}
              style={{ flex:1, background:"none", border:"none", borderBottom:`2.5px solid ${tab===t.id?T.primary:"transparent"}`, padding:"10px 14px", fontWeight:tab===t.id?700:500, fontSize:"0.82rem", color:tab===t.id?T.primary:T.textSub, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ overflowY:"auto", padding:"16px 20px", flex:1 }}>

          {/* ── LIST ── */}
          {tab === "list" && (
            <>
              {loading ? (
                <div style={{ textAlign:"center", padding:40 }}><Loader2 size={28} color={T.primary} style={{ animation:"spin 1s linear infinite" }}/></div>
              ) : cats.length === 0 ? (
                <div style={{ textAlign:"center", padding:40 }}>
                  <Layers size={40} color={T.border} style={{ marginBottom:12 }}/>
                  <div style={{ color:T.textMuted, marginBottom:16 }}>Koi categories nahi</div>
                  <button onClick={seedDefaults} disabled={saving}
                    style={{ background:T.primary, border:"none", borderRadius:10, padding:"10px 20px", color:"#fff", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {saving ? "Adding..." : "🛒 Add Blinkit-style Categories"}
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={seedDefaults} disabled={saving}
                    style={{ width:"100%", background:T.purpleLight, border:`1px dashed ${T.purple}`, color:T.purple, borderRadius:10, padding:"8px", fontWeight:600, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    <Layers size={13}/> Add Default Blinkit Categories
                  </button>

                  {cats.map(cat => (
                    <div key={cat._id} style={{ marginBottom:12 }}>
                      {/* Main Category Row */}
                      <div style={{ display:"flex", alignItems:"center", gap:10, background:T.bg, borderRadius:12, padding:"10px 12px", cursor:"pointer" }}
                        onClick={() => setActiveMain(activeMain===cat._id ? null : cat._id)}>
                        <div style={{ width:36, height:36, borderRadius:10, background:cat.color||T.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                          {cat.icon||"🛒"}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:"0.88rem", color:T.text }}>{cat.name}</div>
                          <div style={{ fontSize:"0.7rem", color:T.textMuted }}>{cat.subCategories?.length||0} sub categories</div>
                        </div>
                        <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                          <button onClick={e=>{e.stopPropagation();startEditMain(cat);}} style={{ background:"none", border:"none", cursor:"pointer", color:T.primary, padding:4 }}><Edit2 size={14}/></button>
                          <button onClick={e=>{e.stopPropagation();deleteMain(cat._id);}} style={{ background:"none", border:"none", cursor:"pointer", color:T.red, padding:4 }}><Trash2 size={14}/></button>
                          <ChevronDown size={15} color={T.textMuted} style={{ transform:activeMain===cat._id?"rotate(180deg)":"none", transition:"transform .2s" }}/>
                        </div>
                      </div>

                      {/* Sub Categories */}
                      {activeMain === cat._id && (
                        <div style={{ marginLeft:20, marginTop:6, display:"flex", flexDirection:"column", gap:6 }}>
                          {(cat.subCategories||[]).map(sub => (
                            <div key={sub._id} style={{ display:"flex", alignItems:"center", gap:8, background:T.white, borderRadius:10, padding:"8px 12px", border:`1px solid ${T.border}` }}>
                              <span style={{ fontSize:16 }}>{sub.icon||"📦"}</span>
                              <span style={{ flex:1, fontWeight:600, fontSize:"0.83rem", color:T.text }}>{sub.name}</span>
                              <button onClick={() => startEditSub(sub, cat._id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.primary, padding:4 }}><Edit2 size={13}/></button>
                              <button onClick={() => deleteSub(sub._id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.red, padding:4 }}><Trash2 size={13}/></button>
                            </div>
                          ))}
                          <button onClick={() => { setSubForm({name:"",icon:"📦",mainCategory:cat._id}); setTab("addSub"); }}
                            style={{ background:"none", border:`1.5px dashed ${T.green}`, borderRadius:10, padding:"7px", color:T.green, fontWeight:600, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:5 }}>
                            <Plus size={13}/> Add Sub Category
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* ── ADD MAIN CATEGORY ── */}
          {tab === "addMain" && (
            <div>
              <div style={{ fontWeight:700, fontSize:"0.9rem", color:T.text, marginBottom:16 }}>
                {editItem ? "Edit Main Category" : "New Main Category"}
              </div>
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Name *</div>
                <input value={mainForm.name} onChange={e=>setMainForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Grocery & Kitchen"
                  style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Icon (Emoji)</div>
                  <input value={mainForm.icon} onChange={e=>setMainForm(f=>({...f,icon:e.target.value}))} placeholder="🛒"
                    style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"1.3rem", background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
                </div>
                <div>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Color</div>
                  <input type="color" value={mainForm.color||"#16a34a"} onChange={e=>setMainForm(f=>({...f,color:e.target.value}))}
                    style={{ width:"100%", height:42, border:`1.5px solid ${T.border}`, borderRadius:10, padding:4, background:T.bg, cursor:"pointer" }}/>
                </div>
              </div>
              {/* Image URL */}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Category Image URL <span style={{ fontWeight:400, color:T.textMuted }}>(optional)</span></div>
                <input value={mainForm.image||""} onChange={e=>setMainForm(f=>({...f,image:e.target.value}))} placeholder="https://cdn.example.com/grocery.png"
                  style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
                {mainForm.image && (
                  <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8 }}>
                    <img src={mainForm.image} alt="preview" onError={e=>e.target.style.display="none"}
                      style={{ width:44, height:44, objectFit:"cover", borderRadius:10, border:`1px solid ${T.border}` }}/>
                    <span style={{ fontSize:"0.7rem", color:T.textSub }}>Preview</span>
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={()=>setTab("list")} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"10px", fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:T.textSub }}>Cancel</button>
                <button onClick={saveMain} disabled={saving} style={{ flex:2, background:T.primary, border:"none", borderRadius:12, padding:"10px", fontWeight:800, cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                  {saving ? <><Loader2 size={14} style={{animation:"spin .75s linear infinite"}}/> Saving...</> : <><Check size={14}/> {editItem?"Update":"Add"} Category</>}
                </button>
              </div>
            </div>
          )}

          {/* ── ADD SUB CATEGORY ── */}
          {tab === "addSub" && (
            <div>
              <div style={{ fontWeight:700, fontSize:"0.9rem", color:T.text, marginBottom:16 }}>
                {editItem ? "Edit Sub Category" : "New Sub Category"}
              </div>
              <Select label="Main Category *" value={subForm.mainCategory}
                onChange={v=>setSubForm(f=>({...f,mainCategory:v}))}
                placeholder="Select main category..."
                options={cats.map(c=>({ value:c._id, label:`${c.icon} ${c.name}` }))}/>
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Name *</div>
                <input value={subForm.name} onChange={e=>setSubForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Vegetables & Fruits"
                  style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Icon (Emoji)</div>
                  <input value={subForm.icon} onChange={e=>setSubForm(f=>({...f,icon:e.target.value}))} placeholder="🥬"
                    style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"1.3rem", background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
                </div>
                <div style={{ display:"flex", flexDirection:"column", justifyContent:"center" }}>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Preview</div>
                  <div style={{ width:44, height:44, borderRadius:10, background:subForm.image?"transparent":(cats.find(c=>c._id===subForm.mainCategory)?.color||"#16a34a")+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.6rem", border:`1px solid ${T.border}` }}>
                    {subForm.image ? <img src={subForm.image} alt="" style={{ width:40, height:40, objectFit:"cover", borderRadius:8 }} onError={e=>e.target.style.display="none"}/> : subForm.icon||"📦"}
                  </div>
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>Sub Category Image URL <span style={{ fontWeight:400, color:T.textMuted }}>(optional)</span></div>
                <input value={subForm.image||""} onChange={e=>setSubForm(f=>({...f,image:e.target.value}))} placeholder="https://cdn.example.com/vegetables.png"
                  style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={()=>setTab("list")} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"10px", fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:T.textSub }}>Cancel</button>
                <button onClick={saveSub} disabled={saving} style={{ flex:2, background:T.green, border:"none", borderRadius:12, padding:"10px", fontWeight:800, cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                  {saving ? <><Loader2 size={14} style={{animation:"spin .75s linear infinite"}}/> Saving...</> : <><Check size={14}/> {editItem?"Update":"Add"} Sub Category</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════
// GROCERY BULK UPLOAD COMPONENT
// ════════════════════════════════════════════════
function GroceryBulkUpload({ storeId, storeName, onSuccess, T }) {
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState("");
  const fileRef = useRef(null);

  const BASE     = typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";
  const getToken = () => localStorage.getItem("adminToken") || localStorage.getItem("token") || "";

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = `${BASE}/grocery/bulk/template`;
    a.setAttribute("download", "grocery_template.xlsx");
    a.click();
  };

  const handleUpload = async () => {
    if (!file) return setError("Pehle file select karo.");
    setUploading(true); setError(""); setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res  = await fetch(`${BASE}/grocery/stores/${storeId}/bulk`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body:    formData,
      });
      const data = await res.json();
      if (data.success) { setResult(data); onSuccess(); }
      else setError(data.message || "Upload failed");
    } catch (e) { setError(e.message || "Upload failed"); }
    setUploading(false);
  };

  return (
    <div>
      {/* Download template */}
      <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:12, padding:"14px 16px", marginBottom:16 }}>
        <div style={{ fontWeight:700, fontSize:"0.82rem", color:T.blue, marginBottom:6 }}>
          📥 Step 1 — Download Template
        </div>
        <div style={{ fontSize:"0.77rem", color:"#374151", marginBottom:10, lineHeight:1.6 }}>
          Excel mein columns: <strong>name, price, category, unit, stock, brand, description, mrp, isAvailable, image</strong>
        </div>
        <button onClick={downloadTemplate} style={{ display:"flex", alignItems:"center", gap:6, background:T.blue, color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:"0.8rem", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          <Download size={14}/> Download Excel Template
        </button>
      </div>

      {/* Upload */}
      <div style={{ fontWeight:700, fontSize:"0.82rem", color:T.text, marginBottom:10 }}>
        📤 Step 2 — Fill & Upload
      </div>

      <div
        onClick={() => fileRef.current?.click()}
        style={{
          border:`2px dashed ${file ? T.green : T.border}`,
          borderRadius:12, padding:"28px 20px",
          textAlign:"center", cursor:"pointer",
          background: file ? T.greenLight : "#fafafa",
          marginBottom:14, transition:"all 0.2s",
        }}
      >
        <Upload size={28} color={file ? T.green : T.textMuted} style={{ margin:"0 auto 10px" }}/>
        {file ? (
          <div>
            <div style={{ fontWeight:700, color:T.green, fontSize:"0.85rem" }}>{file.name}</div>
            <div style={{ fontSize:"0.73rem", color:T.textMuted, marginTop:4 }}>
              {(file.size/1024).toFixed(1)} KB · Click to change
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontWeight:600, color:T.text, fontSize:"0.85rem" }}>Click to select file</div>
            <div style={{ fontSize:"0.73rem", color:T.textMuted, marginTop:4 }}>.csv, .xlsx, .xls · Max 5MB</div>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:"none" }}
          onChange={e => { setFile(e.target.files[0]); setResult(null); setError(""); }}/>
      </div>

      {error && (
        <div style={{ background:T.redLight, border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:"0.8rem", color:T.red, fontWeight:600 }}>
          ❌ {error}
        </div>
      )}

      {result && (
        <div style={{ background:T.greenLight, border:`1px solid #bbf7d0`, borderRadius:10, padding:"12px 16px", marginBottom:12 }}>
          <div style={{ fontWeight:700, color:T.green, marginBottom:6, display:"flex", alignItems:"center", gap:6 }}>
            <Check size={15}/> {result.message}
          </div>
          <div style={{ fontSize:"0.77rem", color:"#374151" }}>
            ✅ {result.inserted} products added
            {result.skipped > 0 && <span> · ⚠️ {result.skipped} skipped</span>}
          </div>
          {result.errors?.length > 0 && (
            <div style={{ marginTop:8, fontSize:"0.73rem", color:T.red }}>
              {result.errors.slice(0,5).map((e,i) => <div key={i}>• {e}</div>)}
              {result.errors.length > 5 && <div>...aur {result.errors.length-5} errors</div>}
            </div>
          )}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        style={{
          width:"100%", padding:"13px",
          background: (!file||uploading) ? "#e5e7eb" : `linear-gradient(135deg,#16a34a,#22c55e)`,
          color: (!file||uploading) ? "#9ca3af" : "#fff",
          border:"none", borderRadius:12, fontWeight:800, fontSize:"0.9rem",
          cursor: (!file||uploading) ? "not-allowed" : "pointer",
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          fontFamily:"inherit",
          boxShadow: (!file||uploading) ? "none" : "0 4px 14px rgba(22,163,74,0.3)",
        }}
      >
        {uploading
          ? <><Loader2 size={16} style={{ animation:"spin 1s linear infinite" }}/> Uploading...</>
          : <><Upload size={16}/> Upload & Add Products</>
        }
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════
// PRODUCTS MODAL — with category dropdowns
// ════════════════════════════════════════════════
const EMPTY_PRODUCT = { name:"", price:"", mrp:"", unit:"", mainCategory:"", subCategory:"", inStock:true, description:"", image:"" };

function ProductsModal({ store, onClose }) {
  const [products,  setProducts]  = useState([]);
  const [cats,      setCats]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [form,      setForm]      = useState(EMPTY_PRODUCT);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [tab,       setTab]       = useState("list");
  const [filterCat, setFilterCat] = useState(""); // filter by mainCategory

  const setF = (k, v) => setForm(f => {
    const updated = { ...f, [k]: v };
    // Sub category reset when main changes
    if (k === "mainCategory") updated.subCategory = "";
    return updated;
  });

  const loadAll = async () => {
    setLoading(true);
    const [pRes, cRes] = await Promise.all([
      apiCall("GET", `/grocery/stores/${store._id}/products`),
      apiCall("GET", "/categories"),
    ]);
    if (pRes.success) setProducts(pRes.data || pRes.products || []);
    if (cRes.success) setCats(cRes.categories || []);
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // Sub categories for selected main
  const subCats = cats.find(c => c._id === form.mainCategory)?.subCategories || [];

  const handleSave = async () => {
    if (!form.name || !form.price) { alert("Name aur price required!"); return; }
    setSaving(true);
    const payload = {
      ...form,
      store: store._id,
      mainCategory: form.mainCategory || undefined,
      subCategory:  form.subCategory  || undefined,
    };
    let res;
    if (editId) {
      res = await apiCall("PUT", `/grocery/items/${editId}`, payload);
    } else {
      res = await apiCall("POST", "/grocery/items", payload);
    }
    setSaving(false);
    if (res.success) {
      await loadAll();
      setForm(EMPTY_PRODUCT); setEditId(null); setTab("list");
    } else alert(res.message || "Error saving!");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await apiCall("DELETE", `/grocery/items/${id}`);
    setProducts(p => p.filter(x => x._id !== id));
  };

  const handleEdit = (p) => {
    setForm({
      name:         p.name,
      price:        p.price,
      mrp:          p.mrp||"",
      unit:         p.unit||"",
      mainCategory: p.mainCategory?._id || p.mainCategory || "",
      subCategory:  p.subCategory?._id  || p.subCategory  || "",
      inStock:      p.inStock,
      description:  p.description||"",
      image:        p.image||"",
    });
    setEditId(p._id); setTab("add");
  };

  // Filtered products by category
  const filtered = filterCat
    ? products.filter(p => (p.mainCategory?._id||p.mainCategory) === filterCat)
    : products;

  // Get cat name helper
  const getCatName = (p) => {
    const main = cats.find(c => c._id === (p.mainCategory?._id||p.mainCategory));
    const sub  = main?.subCategories?.find(s => s._id === (p.subCategory?._id||p.subCategory));
    if (main && sub)  return `${main.icon} ${main.name} > ${sub.icon} ${sub.name}`;
    if (main)         return `${main.icon} ${main.name}`;
    return p.category || "";
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.white, borderRadius:20, width:"100%", maxWidth:580, maxHeight:"92vh", display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1rem", color:T.text }}>{store.name} — Products</div>
            <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>{products.length} products</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:T.textMuted }}><X size={20}/></button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}` }}>
          {["list","add","bulk"].map(t => (
            <button key={t} onClick={() => { setTab(t); if(t==="add"){setForm(EMPTY_PRODUCT);setEditId(null);} }}
              style={{ flex:1, background:"none", border:"none", borderBottom:`2.5px solid ${tab===t?T.primary:"transparent"}`, padding:"10px", fontWeight:tab===t?700:500, fontSize:"0.82rem", color:tab===t?T.primary:T.textSub, cursor:"pointer", fontFamily:"inherit" }}>
              {t==="list" ? "Products List" : t==="add" ? (editId ? "Edit Product" : "Add Product") : "📤 Bulk Upload"}
            </button>
          ))}
        </div>

        <div style={{ overflowY:"auto", padding:"16px 20px", flex:1 }}>

          {/* ── BULK UPLOAD TAB ── */}
          {tab === "bulk" && (
            <GroceryBulkUpload storeId={store._id} storeName={store.name} onSuccess={loadAll} T={T}/>
          )}

          {/* ── LIST TAB ── */}
          {tab === "list" && (
            <>
              {loading ? (
                <div style={{ textAlign:"center", padding:40 }}><Loader2 size={28} color={T.primary} style={{ animation:"spin 1s linear infinite" }}/></div>
              ) : (
                <>
                  <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
                    <button onClick={() => setTab("add")}
                      style={{ background:T.green, border:"none", borderRadius:10, padding:"8px 16px", color:"#fff", fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, fontSize:"0.82rem" }}>
                      <Plus size={14}/> Add Product
                    </button>
                    {/* Category filter */}
                    {cats.length > 0 && (
                      <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                        style={{ flex:1, border:`1.5px solid ${T.border}`, borderRadius:10, padding:"8px 10px", fontSize:"0.8rem", color:T.text, background:T.white, outline:"none", fontFamily:"inherit" }}>
                        <option value="">All Categories</option>
                        {cats.map(c => <option key={c._id} value={c._id}>{c.icon} {c.name}</option>)}
                      </select>
                    )}
                  </div>

                  {filtered.length === 0 ? (
                    <div style={{ textAlign:"center", padding:40 }}>
                      <Package size={40} color={T.border} style={{ marginBottom:12 }}/>
                      <div style={{ color:T.textMuted, marginBottom:12 }}>Koi products nahi</div>
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {filtered.map(p => (
                        <div key={p._id} style={{ background:T.bg, borderRadius:12, padding:"11px 14px", display:"flex", alignItems:"center", gap:12 }}>
                          {p.image ? (
                            <img src={p.image} alt={p.name} onError={e=>e.target.style.display="none"}
                              style={{ width:42, height:42, objectFit:"cover", borderRadius:8, flexShrink:0 }}/>
                          ) : (
                            <div style={{ width:42, height:42, borderRadius:8, background:T.white, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, border:`1px solid ${T.border}` }}>
                              <Package size={18} color={T.textMuted}/>
                            </div>
                          )}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, fontSize:"0.85rem", color:T.text }}>{p.name}</div>
                            <div style={{ fontSize:"0.7rem", color:T.textSub, marginTop:2 }}>
                              ₹{p.price}{p.mrp?` / MRP ₹${p.mrp}`:""} {p.unit?`· ${p.unit}`:""}
                            </div>
                            {getCatName(p) && (
                              <div style={{ fontSize:"0.65rem", color:T.purple, marginTop:2, background:T.purpleLight, display:"inline-block", padding:"1px 7px", borderRadius:100 }}>
                                {getCatName(p)}
                              </div>
                            )}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:4, background:p.inStock?T.greenLight:T.redLight, color:p.inStock?T.green:T.red, padding:"2px 8px", borderRadius:100, fontSize:"0.65rem", fontWeight:700, flexShrink:0 }}>
                            {p.inStock?"In Stock":"Out"}
                          </div>
                          <button onClick={() => handleEdit(p)} style={{ background:"none", border:"none", cursor:"pointer", color:T.primary, padding:4 }}><Edit2 size={15}/></button>
                          <button onClick={() => handleDelete(p._id)} style={{ background:"none", border:"none", cursor:"pointer", color:T.red, padding:4 }}><Trash2 size={15}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── ADD/EDIT TAB ── */}
          {tab === "add" && (
            <div>
              {/* ✅ Main Category Dropdown */}
              <Select label="Main Category" value={form.mainCategory}
                onChange={v => setF("mainCategory", v)}
                placeholder="Select main category..."
                options={cats.map(c => ({ value:c._id, label:`${c.icon} ${c.name}` }))}/>

              {/* ✅ Sub Category Dropdown — changes based on main */}
              {form.mainCategory && (
                <Select label="Sub Category" value={form.subCategory}
                  onChange={v => setF("subCategory", v)}
                  placeholder="Select sub category..."
                  options={subCats.map(s => ({ value:s._id, label:`${s.icon} ${s.name}` }))}/>
              )}

              {[
                { label:"Product Name *", k:"name",        placeholder:"e.g. Amul Milk 500ml" },
                { label:"Price (₹) *",   k:"price",       placeholder:"28", type:"number" },
                { label:"MRP (₹)",       k:"mrp",         placeholder:"32", type:"number" },
                { label:"Unit / Weight", k:"unit",        placeholder:"e.g. 500ml, 1kg, 6 pcs" },
                { label:"Image URL",     k:"image",       placeholder:"https://..." },
                { label:"Description",   k:"description", placeholder:"Short description (optional)" },
              ].map(({ label, k, placeholder, type="text" }) => (
                <div key={k}>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:T.textSub, marginBottom:5 }}>{label}</div>
                  <input type={type} value={form[k]??""} onChange={e=>setF(k, type==="number"?Number(e.target.value):e.target.value)} placeholder={placeholder}
                    style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:10, padding:"9px 12px", fontSize:"0.85rem", color:T.text, background:T.bg, outline:"none", fontFamily:"inherit", marginBottom:12, boxSizing:"border-box" }}/>
                </div>
              ))}

              {form.image && (
                <div style={{ marginBottom:12, textAlign:"center" }}>
                  <img src={form.image} alt="preview" onError={e=>e.target.style.display="none"}
                    style={{ width:80, height:80, objectFit:"cover", borderRadius:12, border:`1.5px solid ${T.border}` }}/>
                </div>
              )}

              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderTop:`1px solid ${T.border}`, marginBottom:16 }}>
                <span style={{ fontWeight:600, fontSize:"0.85rem", color:T.text }}>In Stock</span>
                <div onClick={() => setF("inStock", !form.inStock)} style={{ cursor:"pointer", color:form.inStock?T.green:T.textMuted }}>
                  {form.inStock ? <ToggleRight size={26}/> : <ToggleLeft size={26}/>}
                </div>
              </div>

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => { setTab("list"); setForm(EMPTY_PRODUCT); setEditId(null); }}
                  style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"10px", fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:T.textSub }}>Cancel</button>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex:2, background:T.green, border:"none", borderRadius:12, padding:"10px", fontWeight:800, cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                  {saving ? <><Loader2 size={14} style={{animation:"spin .75s linear infinite"}}/> Saving...</> : <><Check size={14}/> {editId?"Update":"Add"} Product</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════
// STORE FORM
// ════════════════════════════════════════════════
function StoreForm({ store, onSave, onClose }) {
  const [form,     setForm]     = useState(store ? { ...store, lat:store.location?.coordinates?.[1]||store.lat||"", lng:store.location?.coordinates?.[0]||store.lng||"" } : EMPTY_STORE);
  const [saving,   setSaving]   = useState(false);
  const [locating, setLocating] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchLocation = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      pos => { set("lat", pos.coords.latitude.toFixed(6)); set("lng", pos.coords.longitude.toFixed(6)); setLocating(false); },
      ()  => { alert("Location access denied!"); setLocating(false); }
    );
  };

  const handle = async () => {
    if (!form.name || !form.area) { alert("Store name aur area required!"); return; }
    setSaving(true);
    const res = form._id
      ? await apiCall("PUT",  `/grocery/stores/${form._id}`, form)
      : await apiCall("POST", "/grocery/stores", form);
    setSaving(false);
    if (res.success) onSave(res.data, !!form._id);
    else alert(res.message || "Error saving store!");
  };

  return (
    <div>
      <F label="Store Name *"  k="name"       placeholder="e.g. D-Mart Akola"    form={form} set={set}/>
      <F label="Area *"        k="area"       placeholder="e.g. Civil Lines"     form={form} set={set}/>
      <F label="Full Address"  k="address"    placeholder="Near Railway Station, Akola" form={form} set={set}/>
      <F label="Phone"         k="phone"      placeholder="9876543210"           form={form} set={set}/>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
        <F label="Delivery (min)" k="deliveryTime" type="number" form={form} set={set}/>
        <F label="Fee (₹)"        k="deliveryFee"  type="number" form={form} set={set}/>
        <F label="Min Order (₹)"  k="minOrder"     type="number" form={form} set={set}/>
      </div>

      {/* Location */}
      <div style={{ background:T.blueLight, border:`1px solid #bfdbfe`, borderRadius:12, padding:"12px 14px", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
          <div style={{ fontSize:"0.72rem", fontWeight:800, color:T.blue, textTransform:"uppercase", letterSpacing:"0.5px" }}>📍 Store Location</div>
          <button type="button" onClick={fetchLocation} disabled={locating}
            style={{ background:T.blue, border:"none", borderRadius:8, padding:"5px 12px", color:"#fff", fontWeight:700, fontSize:"0.72rem", cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:"inherit", opacity:locating?0.7:1 }}>
            {locating ? <Loader2 size={12} style={{animation:"spin .75s linear infinite"}}/> : <Navigation size={12}/>}
            {locating ? "Fetching..." : "Use GPS"}
          </button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          <div>
            <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, marginBottom:4 }}>Latitude</div>
            <input type="text" value={form.lat||""} onChange={e=>set("lat",e.target.value)} placeholder="20.7002"
              style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:8, padding:"8px 10px", fontSize:"0.82rem", color:T.text, background:T.white, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
          </div>
          <div>
            <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, marginBottom:4 }}>Longitude</div>
            <input type="text" value={form.lng||""} onChange={e=>set("lng",e.target.value)} placeholder="77.0082"
              style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:8, padding:"8px 10px", fontSize:"0.82rem", color:T.text, background:T.white, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}/>
          </div>
          <div>
            <div style={{ fontSize:"0.68rem", fontWeight:700, color:T.textSub, marginBottom:4 }}>Radius (km)</div>
            <input type="number" value={form.radius||10} onChange={e=>set("radius",Number(e.target.value))} min={1} max={50}
              style={{ width:"100%", border:`1.5px solid ${T.border}`, borderRadius:8, padding:"8px 10px", fontSize:"0.82rem", color:T.text, background:T.white, outline:"none", fontFamily:"inherit", boxSizing:"border-box", textAlign:"center" }}/>
          </div>
        </div>
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderTop:`1px solid ${T.border}`, marginBottom:16 }}>
        <span style={{ fontWeight:600, fontSize:"0.85rem", color:T.text }}>Store is Open</span>
        <div onClick={() => set("isOpen", !form.isOpen)} style={{ cursor:"pointer", color:form.isOpen?T.green:T.textMuted }}>
          {form.isOpen ? <ToggleRight size={26}/> : <ToggleLeft size={26}/>}
        </div>
      </div>

      <div style={{ display:"flex", gap:10 }}>
        <button onClick={onClose} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"10px", fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:T.textSub }}>Cancel</button>
        <button onClick={handle} disabled={saving} style={{ flex:2, background:T.green, border:"none", borderRadius:12, padding:"10px", fontWeight:800, cursor:"pointer", color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
          {saving ? <><Loader2 size={14} style={{animation:"spin .75s linear infinite"}}/> Saving...</> : <><Check size={14}/> {store?"Update Store":"Add Store"}</>}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════
export default function GroceryStores() {
  const [stores,        setStores]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [search,        setSearch]        = useState("");
  const [modal,         setModal]         = useState(null);
  const [delId,         setDelId]         = useState(null);
  const [toast,         setToast]         = useState("");
  const [productsStore, setProductsStore] = useState(null);
  const [showCatMgr,    setShowCatMgr]    = useState(false);

  const showToast = m => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const loadStores = async () => {
    setLoading(true); setError(null);
    const res = await apiCall("GET", "/grocery/stores");
    if (res.success) setStores(res.data || []);
    else setError(res.message || "Backend se connect nahi hua");
    setLoading(false);
  };

  useEffect(() => { loadStores(); }, []);

  const handleToggle = async (id) => {
    const res = await apiCall("PATCH", `/grocery/stores/${id}/toggle`);
    if (res.success) { setStores(s => s.map(x => x._id===id ? res.data : x)); showToast(`Store ${res.data.isOpen?"Open":"Closed"}!`); }
  };

  const handleSave = (data, isEdit) => {
    if (isEdit) setStores(s => s.map(x => x._id===data._id ? data : x));
    else        setStores(s => [data, ...s]);
    setModal(null);
    showToast(isEdit ? "Store updated!" : "Store added!");
  };

  const handleDelete = async () => {
    const res = await apiCall("DELETE", `/grocery/stores/${delId}`);
    if (res.success) { setStores(s => s.filter(x => x._id!==delId)); showToast("Store deleted!"); }
    setDelId(null);
  };

  const filtered = stores.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.area?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>

      {toast && (
        <div style={{ position:"fixed", top:80, left:"50%", transform:"translateX(-50%)", background:T.text, color:"#fff", padding:"10px 20px", borderRadius:100, fontSize:"0.82rem", fontWeight:600, zIndex:600, display:"flex", alignItems:"center", gap:7, whiteSpace:"nowrap" }}>
          <Check size={14} color={T.green}/> {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", color:T.text }}>Grocery Stores</div>
          <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>{stores.length} stores in Akola</div>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:"8px 12px" }}>
            <Search size={14} color={T.textMuted}/>
            <input placeholder="Search stores..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{ border:"none", background:"transparent", color:T.text, fontSize:"0.83rem", outline:"none", fontFamily:"inherit", width:130 }}/>
          </div>
          <button onClick={loadStores} style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:10, padding:"8px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:"0.78rem", fontWeight:600, color:T.textSub, fontFamily:"inherit" }}>
            <RefreshCw size={14} style={{ animation:loading?"spin 1s linear infinite":"none" }}/>
          </button>

          {/* ✅ Category Manager button */}
          <button onClick={() => setShowCatMgr(true)}
            style={{ background:T.purpleLight, border:`1px solid ${T.purple}22`, borderRadius:10, padding:"8px 14px", fontWeight:700, fontSize:"0.82rem", color:T.purple, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" }}>
            <Layers size={14}/> Categories
          </button>

          <button onClick={() => setModal("add")}
            style={{ background:T.green, border:"none", borderRadius:10, padding:"9px 16px", fontWeight:700, fontSize:"0.85rem", color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" }}>
            <Plus size={16}/> Add Store
          </button>
        </div>
      </div>

      {/* Loading / Error / Empty */}
      {loading && <div style={{ textAlign:"center", padding:60 }}><Loader2 size={32} color={T.primary} style={{ animation:"spin 1s linear infinite" }}/></div>}
      {!loading && error && (
        <div style={{ background:T.redLight, border:`1px solid #fecaca`, borderRadius:16, padding:"20px", display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <AlertCircle size={20} color={T.red}/>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:T.red }}>Backend connect nahi hua</div>
            <div style={{ fontSize:"0.78rem", color:T.red }}>{error}</div>
          </div>
          <button onClick={loadStores} style={{ background:T.red, border:"none", borderRadius:10, padding:"8px 16px", color:"#fff", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Retry</button>
        </div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div style={{ textAlign:"center", padding:60 }}>
          <ShoppingBasket size={44} color={T.border} style={{ marginBottom:12 }}/>
          <div style={{ color:T.textMuted, fontWeight:600, marginBottom:8 }}>
            {search ? `"${search}" se koi store nahi mila` : "Koi stores nahi — Add karo!"}
          </div>
          {!search && <button onClick={()=>setModal("add")} style={{ background:T.green, border:"none", borderRadius:10, padding:"10px 20px", color:"#fff", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>+ Add First Store</button>}
        </div>
      )}

      {/* Store Cards */}
      {!loading && !error && filtered.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:14 }}>
          {filtered.map((store, i) => (
            <div key={store._id} style={{ background:T.white, borderRadius:16, border:`1.5px solid ${store.isOpen?T.border:"#fecaca"}`, padding:16, animation:`fadeUp .3s ${i*.05}s ease both` }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:"linear-gradient(135deg,#16a34a,#22c55e)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <ShoppingBasket size={20} color="rgba(255,255,255,0.9)"/>
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:"0.9rem", color:T.text }}>{store.name}</div>
                    <div style={{ fontSize:"0.72rem", color:T.textSub, display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                      <MapPin size={11} color={T.textMuted}/> {store.area}
                    </div>
                  </div>
                </div>
                <div onClick={()=>handleToggle(store._id)}
                  style={{ cursor:"pointer", display:"inline-flex", alignItems:"center", gap:5, background:store.isOpen?T.greenLight:T.redLight, color:store.isOpen?T.green:T.red, padding:"3px 10px", borderRadius:100, fontSize:"0.68rem", fontWeight:700, flexShrink:0 }}>
                  <div style={{ width:6, height:6, borderRadius:"50%", background:store.isOpen?T.green:T.red }}/>
                  {store.isOpen?"Open":"Closed"}
                </div>
              </div>

              {store.location?.coordinates?.[0] ? (
                <div style={{ fontSize:"0.68rem", color:T.blue, background:T.blueLight, borderRadius:6, padding:"3px 8px", display:"inline-flex", alignItems:"center", gap:4, marginBottom:10 }}>
                  <MapPin size={10}/> Location set ✓
                </div>
              ) : (
                <div style={{ fontSize:"0.68rem", color:T.yellow, background:"#fefce8", borderRadius:6, padding:"3px 8px", display:"inline-flex", alignItems:"center", gap:4, marginBottom:10 }}>
                  ⚠️ Location not set
                </div>
              )}

              <div style={{ display:"flex", gap:8, marginBottom:12 }}>
                {[
                  { l:"Delivery", v:`${store.deliveryTime||45} min`, icon:Clock  },
                  { l:"Fee",      v:store.deliveryFee===0?"Free":`₹${store.deliveryFee}`, icon:Truck },
                  { l:"Radius",   v:`${store.radius||10} km`, icon:Navigation },
                ].map((s,j) => (
                  <div key={j} style={{ flex:1, background:T.bg, borderRadius:8, padding:"7px", textAlign:"center" }}>
                    <div style={{ fontWeight:700, fontSize:"0.8rem", color:T.text }}>{s.v}</div>
                    <div style={{ fontSize:"0.6rem", color:T.textMuted }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {store.phone && (
                <div style={{ fontSize:"0.72rem", color:T.textSub, marginBottom:12, display:"flex", alignItems:"center", gap:5 }}>
                  <Phone size={11} color={T.textMuted}/> {store.phone}
                </div>
              )}

              <div style={{ display:"flex", gap:8 }}>
                <button onClick={()=>setProductsStore(store)}
                  style={{ flex:1, background:T.greenLight, border:"none", borderRadius:9, padding:"8px", fontWeight:700, fontSize:"0.78rem", cursor:"pointer", color:T.green, display:"flex", alignItems:"center", justifyContent:"center", gap:5, fontFamily:"inherit" }}>
                  <Package size={13}/> Products
                </button>
                <button onClick={()=>setModal(store)}
                  style={{ flex:1, background:T.primaryLight, border:"none", borderRadius:9, padding:"8px", fontWeight:700, fontSize:"0.78rem", cursor:"pointer", color:T.primary, display:"flex", alignItems:"center", justifyContent:"center", gap:5, fontFamily:"inherit" }}>
                  <Edit2 size={13}/> Edit
                </button>
                <button onClick={()=>setDelId(store._id)}
                  style={{ flex:1, background:T.redLight, border:"none", borderRadius:9, padding:"8px", fontWeight:700, fontSize:"0.78rem", cursor:"pointer", color:T.red, display:"flex", alignItems:"center", justifyContent:"center", gap:5, fontFamily:"inherit" }}>
                  <Trash2 size={13}/> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal && (
        <div onClick={()=>setModal(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:T.white, borderRadius:20, width:"100%", maxWidth:500, padding:24, boxShadow:"0 20px 60px rgba(0,0,0,0.15)", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.05rem", marginBottom:20, color:T.text }}>
              {modal==="add" ? "Add Grocery Store" : "Edit Store"}
            </div>
            <StoreForm store={modal==="add"?null:modal} onSave={handleSave} onClose={()=>setModal(null)}/>
          </div>
        </div>
      )}

      {delId && (
        <div onClick={()=>setDelId(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:T.white, borderRadius:20, padding:"28px 24px", maxWidth:320, width:"100%", textAlign:"center" }}>
            <div style={{ width:52, height:52, borderRadius:"50%", background:T.redLight, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
              <Trash2 size={22} color={T.red}/>
            </div>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", marginBottom:8, color:T.text }}>Delete Store?</div>
            <div style={{ fontSize:"0.82rem", color:T.textSub, marginBottom:22, lineHeight:1.6 }}>Ye store permanently delete ho jayega!</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setDelId(null)} style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"11px", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              <button onClick={handleDelete} style={{ flex:1, background:T.red, border:"none", borderRadius:12, padding:"11px", fontWeight:800, cursor:"pointer", color:"#fff", fontFamily:"inherit" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {productsStore && <ProductsModal store={productsStore} onClose={()=>setProductsStore(null)}/>}
      {showCatMgr    && <CategoryManager onClose={()=>setShowCatMgr(false)}/>}
    </div>
  );
}